import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock, ShieldCheck, Layers, GitBranch, UserCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEO from './SEO';

const PILLARS = [
  {
    icon: Lock,
    title: 'Independence',
    body: [
      'MyInvestFolio is an infrastructure platform, not a manager. We do not custody assets, place trades, sell financial products, or receive commissions from any broker, fund, bank, or platform.',
      'Your portfolio view is shaped by your investments — not by what we are paid to recommend. There is no fund supermarket, no broker affiliate revenue, no behavioural nudge towards any particular asset.',
    ],
  },
  {
    icon: ShieldCheck,
    title: 'Privacy',
    body: [
      'Your portfolio is encrypted at rest with a key only the platform can use, transmitted over TLS, and never shared with third parties for marketing, analytics, or training models.',
      'We do not use any large language model, generative AI service, or external data-enrichment vendor on customer data. The platform has no integration that could send your holdings outside the application boundary.',
      'Our own staff cannot view your portfolio identifiably. Anonymisation is the default; identity is revealed only when the founder explicitly elects to view a single account for support or audit, and every such reveal is logged.',
    ],
  },
  {
    icon: Layers,
    title: 'Comprehensiveness',
    body: [
      'A serious portfolio is rarely held in one place or in one name. We support direct holdings, indirect holdings through entities, trusts and foundations, related-asset memoranda, multi-currency native amounts with no forced FX conversion, and forty-plus asset types from quoted equity to gold to convertibles.',
      'The infrastructure reflects how wealth is actually structured, rather than a simplified version.',
    ],
  },
  {
    icon: GitBranch,
    title: 'Continuity',
    body: [
      'A portfolio outlives a financial year — and sometimes outlives its owner. We are building MyInvestFolio so that the structure of your wealth, the entities that hold it, and the documents that describe it remain coherent over decades, across advisers, and across generations.',
      'Continuity System — the layer that quietly helps the people you trust find and understand what you own, if the day ever comes when you\u2019re not available to explain it yourself. Continuity is a longer arc than convenience.',
    ],
  },
];

const PrincipleBlock = ({ icon: Icon, title, body, accent }) => {
  const titleColor = accent === 'blue' ? 'text-blue-400' : 'text-emerald-400';
  const iconColor  = accent === 'blue' ? 'text-blue-400' : 'text-emerald-400';
  const barColor   = accent === 'blue' ? 'bg-blue-500'   : 'bg-emerald-500';
  return (
    <div className="relative bg-gray-800 rounded-xl p-8 border border-gray-700 overflow-hidden" data-testid={`philosophy-${title.toLowerCase()}`}>
      <span aria-hidden className={`absolute left-0 top-0 bottom-0 w-1 ${barColor}`} />
      <div className="flex items-start gap-4">
        <Icon className={`w-10 h-10 ${iconColor} shrink-0 mt-1`} />
        <div>
          <h2 className={`text-2xl font-semibold mb-3 ${titleColor}`}>{title}</h2>
          {body.map((p, i) => (
            <p key={i} className={`text-lg text-gray-300 leading-relaxed ${i < body.length - 1 ? 'mb-4' : ''}`}>{p}</p>
          ))}
        </div>
      </div>
    </div>
  );
};

const OurPhilosophyPage = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-linear-to-br from-gray-900 via-gray-800 to-black text-white">
      <SEO
        title="Our Philosophy"
        description="What MyInvestFolio will and will not do. Independence, privacy, comprehensiveness and continuity — the four principles that shape every product decision."
        path="/our-philosophy"
      />
      <header className="border-b border-gray-700">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => navigate('/')} className="text-gray-300 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
            </Button>
            <span className="text-xl font-bold">My<span className="text-green-600">Invest</span><span className="text-blue-600">Folio</span></span>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 max-w-4xl">
        <div className="text-center mb-14">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 text-emerald-600">Our Philosophy</h1>
          <div className="w-24 h-1 mx-auto bg-linear-to-r from-emerald-500 to-blue-500 mb-6"></div>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">
            Four principles shape every product decision we make. They are deliberately narrow — and they are deliberately what we will be measured against.
          </p>
        </div>

        <div className="space-y-8">
          {PILLARS.map((p, idx) => (
            <PrincipleBlock key={p.title} {...p} accent={idx % 2 === 0 ? 'emerald' : 'blue'} />
          ))}
        </div>

        {/* Founder signature */}
        <div className="mt-16 bg-gray-800/60 border border-gray-700 rounded-xl p-8 text-center">
          <UserCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
          <p className="text-base text-gray-300 italic mb-2 max-w-2xl mx-auto">
            &ldquo;A portfolio is not just what you own. It is a record of your careful decisions &mdash; made over years, sometimes across generations.
            MyInvestFolio is built to help you hold that record together, with the privacy it deserves.&rdquo;
          </p>
          <p className="text-sm text-emerald-400 font-semibold mt-3">Adejare R. OlaOluwa</p>
          <p className="text-xs text-gray-500">Founder, MyInvestFolio</p>
        </div>

        <div className="text-center mt-14">
          <Button onClick={() => navigate('/')} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-8 py-3 rounded-lg shadow-lg">
            Return to Home
          </Button>
        </div>
      </main>
    </div>
  );
};

export default OurPhilosophyPage;
